import { useState } from "react"
import { Layout } from "@/components/Layout"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Plus, TrendingUp, TrendingDown, DollarSign, Receipt, Building2, Eye, Edit, Trash2, FileDown } from "lucide-react"
import { useFinancialCharges, useFinancialStats } from "@/hooks/useFinancialCharges"
import { useSuppliers } from "@/hooks/useSuppliers"
import { useFinancialRevenues, useRevenueStats } from "@/hooks/useFinancialRevenues"
import { useFinancialExpenses, useExpenseStats } from "@/hooks/useFinancialExpenses"
import { CriarCobrancaModal } from "@/components/CriarCobrancaModal"
import { DetalhesCobrancaModal } from "@/components/DetalhesCobrancaModal"
import { CadastrarFornecedorModal } from "@/components/CadastrarFornecedorModal"
import { RegistrarReceitaModal } from "@/components/RegistrarReceitaModal"
import { RegistrarDespesaModal } from "@/components/RegistrarDespesaModal"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { RelatorioFinanceiroSelector } from "@/components/relatorios/RelatorioFinanceiroSelector"
import { formatCurrency } from "@/lib/utils"
import { format } from "date-fns"
import { ptBR } from "date-fns/locale"
import type { FinancialCharge } from "@/hooks/useFinancialCharges"
import type { Supplier } from "@/hooks/useSuppliers"

const Financeiro = () => {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedCharge, setSelectedCharge] = useState<FinancialCharge | null>(null)
  const [showSupplierModal, setShowSupplierModal] = useState(false)
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null)
  const [showRevenueModal, setShowRevenueModal] = useState(false)
  const [showExpenseModal, setShowExpenseModal] = useState(false)
  
  const { data: charges = [] } = useFinancialCharges()
  const { data: stats } = useFinancialStats()
  const { data: suppliers = [] } = useSuppliers()
  const { data: revenues = [] } = useFinancialRevenues()
  const { data: revenueStats } = useRevenueStats()
  const { data: expenses = [] } = useFinancialExpenses()
  const { data: expenseStats } = useExpenseStats()

  const getStatusBadge = (status: string) => {
    const variants = {
      paid: { label: "Pago", className: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100" },
      pending: { label: "Pendente", className: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100" },
      overdue: { label: "Vencida", className: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100" },
      canceled: { label: "Cancelada", className: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100" }
    }
    const variant = variants[status as keyof typeof variants] || variants.pending
    return <Badge className={variant.className}>{variant.label}</Badge>
  }

  const handleViewDetails = (charge: FinancialCharge) => {
    setSelectedCharge(charge)
    setShowDetailsModal(true)
  }

  const handleEditSupplier = (supplier: Supplier) => {
    setSelectedSupplier(supplier)
    setShowSupplierModal(true)
  }

  const totalBalance = (revenueStats?.total_amount || 0) - (expenseStats?.total_amount || 0)

  return (
    <Layout>
      <div className="container mx-auto py-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Gestão Financeira</h1>
          <p className="text-muted-foreground">
            Gerencie cobranças, receitas, despesas e fornecedores do condomínio
          </p>
        </div>

        {/* Dashboard KPIs */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Saldo Total</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${totalBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(totalBalance)}
              </div>
              <p className="text-xs text-muted-foreground">
                Receitas - Despesas
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Receitas</CardTitle>
              <TrendingUp className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(revenueStats?.total_amount || 0)}
              </div>
              <p className="text-xs text-muted-foreground">
                {revenues.length} lançamentos
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Despesas</CardTitle>
              <TrendingDown className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {formatCurrency(expenseStats?.total_amount || 0)}
              </div>
              <p className="text-xs text-muted-foreground">
                {expenses.length} lançamentos
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Cobranças</CardTitle>
              <Receipt className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_charges || 0}</div>
              <p className="text-xs text-muted-foreground">
                {formatCurrency(stats?.total_amount || 0)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs de Gestão */}
        <Tabs defaultValue="charges" className="w-full">
          <TabsList>
            <TabsTrigger value="charges">Cobranças</TabsTrigger>
            <TabsTrigger value="revenues">Receitas</TabsTrigger>
            <TabsTrigger value="expenses">Despesas</TabsTrigger>
            <TabsTrigger value="suppliers">Fornecedores</TabsTrigger>
            <TabsTrigger value="summary">Resumo</TabsTrigger>
            <TabsTrigger value="reports">Relatórios</TabsTrigger>
          </TabsList>

          {/* Tab Cobranças */}
          <TabsContent value="charges" className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold">Cobranças</h2>
                <p className="text-sm text-muted-foreground">Gestão de cobranças e pagamentos</p>
              </div>
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Nova Cobrança
              </Button>
            </div>

            <Card>
              <CardContent className="pt-6">
                {charges.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">Nenhuma cobrança encontrada</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Unidade</TableHead>
                        <TableHead>Descrição</TableHead>
                        <TableHead>Morador</TableHead>
                        <TableHead>Valor</TableHead>
                        <TableHead>Vencimento</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {charges.map((charge) => (
                        <TableRow key={charge.id}>
                          <TableCell>
                            {charge.unit?.building && `${charge.unit.building} - `}
                            {charge.unit?.number}
                          </TableCell>
                          <TableCell>{charge.description}</TableCell>
                          <TableCell>{charge.resident?.full_name || "-"}</TableCell>
                          <TableCell className="font-semibold">
                            {formatCurrency(charge.amount_cents)}
                          </TableCell>
                          <TableCell>
                            {format(new Date(charge.due_date), "dd/MM/yyyy", { locale: ptBR })}
                          </TableCell>
                          <TableCell>{getStatusBadge(charge.status)}</TableCell>
                          <TableCell>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleViewDetails(charge)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Detalhes
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Receitas */}
          <TabsContent value="revenues" className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold">Receitas</h2>
                <p className="text-sm text-muted-foreground">Registro de entradas financeiras</p>
              </div>
              <Button onClick={() => setShowRevenueModal(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Registrar Receita
              </Button>
            </div>

            <Card>
              <CardContent className="pt-6">
                {revenues.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">Nenhuma receita registrada</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Descrição</TableHead>
                        <TableHead>Categoria</TableHead>
                        <TableHead>Unidade</TableHead>
                        <TableHead>Valor</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {revenues.map((revenue) => (
                        <TableRow key={revenue.id}>
                          <TableCell>
                            {format(new Date(revenue.revenue_date), "dd/MM/yyyy", { locale: ptBR })}
                          </TableCell>
                          <TableCell>{revenue.description}</TableCell>
                          <TableCell className="capitalize">{revenue.category}</TableCell>
                          <TableCell>
                            {revenue.unit ? `${revenue.unit.building || ""} ${revenue.unit.number}` : "-"}
                          </TableCell>
                          <TableCell className="font-semibold text-green-600">
                            {formatCurrency(revenue.amount_cents)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Despesas */}
          <TabsContent value="expenses" className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold">Despesas</h2>
                <p className="text-sm text-muted-foreground">Registro de saídas financeiras</p>
              </div>
              <Button onClick={() => setShowExpenseModal(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Registrar Despesa
              </Button>
            </div>

            <Card>
              <CardContent className="pt-6">
                {expenses.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">Nenhuma despesa registrada</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Descrição</TableHead>
                        <TableHead>Categoria</TableHead>
                        <TableHead>Fornecedor</TableHead>
                        <TableHead>Valor</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {expenses.map((expense) => (
                        <TableRow key={expense.id}>
                          <TableCell>
                            {format(new Date(expense.expense_date), "dd/MM/yyyy", { locale: ptBR })}
                          </TableCell>
                          <TableCell>{expense.description}</TableCell>
                          <TableCell className="capitalize">{expense.category}</TableCell>
                          <TableCell>{expense.supplier?.name || "-"}</TableCell>
                          <TableCell className="font-semibold text-red-600">
                            {formatCurrency(expense.amount_cents)}
                          </TableCell>
                          <TableCell>{getStatusBadge(expense.status)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Fornecedores */}
          <TabsContent value="suppliers" className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold">Fornecedores</h2>
                <p className="text-sm text-muted-foreground">Cadastro de fornecedores e prestadores</p>
              </div>
              <Button onClick={() => {
                setSelectedSupplier(null)
                setShowSupplierModal(true)
              }}>
                <Plus className="mr-2 h-4 w-4" />
                Novo Fornecedor
              </Button>
            </div>

            <Card>
              <CardContent className="pt-6">
                {suppliers.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">Nenhum fornecedor cadastrado</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nome</TableHead>
                        <TableHead>Categoria</TableHead>
                        <TableHead>Contato</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {suppliers.map((supplier) => (
                        <TableRow key={supplier.id}>
                          <TableCell className="font-medium">{supplier.name}</TableCell>
                          <TableCell className="capitalize">{supplier.category}</TableCell>
                          <TableCell>
                            {supplier.phone || supplier.email || "-"}
                          </TableCell>
                          <TableCell>
                            <Badge variant={supplier.is_active ? "default" : "secondary"}>
                              {supplier.is_active ? "Ativo" : "Inativo"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditSupplier(supplier)}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Editar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Resumo */}
          <TabsContent value="summary" className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold">Resumo Financeiro</h2>
              <p className="text-sm text-muted-foreground">Visão geral das finanças do condomínio</p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Status das Cobranças</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between">
                    <span>Total de cobranças:</span>
                    <span className="font-bold">{stats?.total_charges || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Pendentes:</span>
                    <span className="font-bold text-yellow-600">{stats?.pending_charges || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Pagas:</span>
                    <span className="font-bold text-green-600">{stats?.paid_charges || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Vencidas:</span>
                    <span className="font-bold text-red-600">{stats?.overdue_charges || 0}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Balanço Financeiro</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between">
                    <span>Total de Receitas:</span>
                    <span className="font-bold text-green-600">
                      {formatCurrency(revenueStats?.total_amount || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total de Despesas:</span>
                    <span className="font-bold text-red-600">
                      {formatCurrency(expenseStats?.total_amount || 0)}
                    </span>
                  </div>
                  <div className="border-t pt-2 flex justify-between">
                    <span className="font-semibold">Saldo:</span>
                    <span className={`font-bold text-xl ${totalBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(totalBalance)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Tab Relatórios */}
          <TabsContent value="reports" className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold">Relatórios Financeiros</h2>
              <p className="text-sm text-muted-foreground">Gere relatórios completos em PDF para análise financeira</p>
            </div>

            <RelatorioFinanceiroSelector />

            <Card>
              <CardHeader>
                <CardTitle>Tipos de Relatórios Disponíveis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="p-3 border rounded-lg">
                    <h4 className="font-semibold mb-1">📊 Resumo Executivo</h4>
                    <p className="text-sm text-muted-foreground">
                      KPIs principais, status de cobranças, despesas por categoria e principais fornecedores
                    </p>
                  </div>
                  
                  <div className="p-3 border rounded-lg">
                    <h4 className="font-semibold mb-1">📝 Relatório Completo (Analítico)</h4>
                    <p className="text-sm text-muted-foreground">
                      Todas as receitas, despesas e cobranças detalhadas do período com totalizadores
                    </p>
                  </div>
                  
                  <div className="p-3 border rounded-lg">
                    <h4 className="font-semibold mb-1">💰 Fluxo de Caixa</h4>
                    <p className="text-sm text-muted-foreground">
                      Movimentação diária, entradas, saídas e saldo acumulado ao longo do período
                    </p>
                  </div>
                  
                  <div className="p-3 border rounded-lg">
                    <h4 className="font-semibold mb-1">⚠️ Inadimplência</h4>
                    <p className="text-sm text-muted-foreground">
                      Lista de débitos vencidos, valor total em atraso e taxa de inadimplência
                    </p>
                  </div>
                  
                  <div className="p-3 border rounded-lg">
                    <h4 className="font-semibold mb-1">🏢 Por Fornecedor</h4>
                    <p className="text-sm text-muted-foreground">
                      Histórico completo de serviços e gastos com fornecedor específico
                    </p>
                  </div>
                  
                  <div className="p-3 border rounded-lg">
                    <h4 className="font-semibold mb-1">📅 Cobranças por Período</h4>
                    <p className="text-sm text-muted-foreground">
                      Detalhamento de todas as cobranças, status de pagamento e taxa de recebimento
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Modais */}
        <CriarCobrancaModal 
          open={showCreateModal} 
          onOpenChange={setShowCreateModal} 
        />
        
        <DetalhesCobrancaModal
          open={showDetailsModal}
          onOpenChange={setShowDetailsModal}
          charge={selectedCharge}
        />

        <CadastrarFornecedorModal
          open={showSupplierModal}
          onOpenChange={setShowSupplierModal}
          supplier={selectedSupplier}
        />

        <RegistrarReceitaModal
          open={showRevenueModal}
          onOpenChange={setShowRevenueModal}
        />

        <RegistrarDespesaModal
          open={showExpenseModal}
          onOpenChange={setShowExpenseModal}
        />
      </div>
    </Layout>
  )
}

export default Financeiro
